import { useState, useEffect } from 'react';
import { useLang } from '../hooks/useLang';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Globe, Thermometer, Zap, Check, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import SectionReveal from '../components/SectionReveal';
import { t } from '../translations';

const HERO_IMG  = 'https://static.prod-images.emergentagent.com/jobs/ed16aff2-3768-4294-8b8e-fb676e7a6422/images/f206875e13a7f404172e6419d59210384695a3f3426c4acb4bc933e7a6936578.png';
const DC_IMG    = 'https://static.prod-images.emergentagent.com/jobs/ed16aff2-3768-4294-8b8e-fb676e7a6422/images/c13fe2a51ba1247a70700053390d5fb9ce5ca7a8428e3633d04240d8a8f3f46e.png';
const STEEL_IMG = 'https://static.prod-images.emergentagent.com/jobs/ed16aff2-3768-4294-8b8e-fb676e7a6422/images/dd6404e23c8e7890487e872db9b92fcca149eed0c44bd53feb6b04a5f4f0596a.png';
const EV_IMG    = 'https://static.prod-images.emergentagent.com/jobs/ed16aff2-3768-4294-8b8e-fb676e7a6422/images/bb43096bfb60e06b8696907233bcb3dfda51fbbbb1fb1e24e8b507b5470cfc62.png';

const PILLAR_ICONS = { Globe, Thermometer, Zap };
const APP_IMAGES   = [DC_IMG, STEEL_IMG, EV_IMG];

// Animation variant constants
const FU_20   = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const FU_30   = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 } };
const FU_24   = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };
const FU_16   = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };
const BACK_MV = { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 } };
const T_05    = { duration: 0.5 };
const T_06_01 = { duration: 0.6, delay: 0.1 };
const T_08_25 = { duration: 0.8, delay: 0.25 };
const T_07_45 = { duration: 0.7, delay: 0.45 };
const T_06_65 = { duration: 0.6, delay: 0.65 };

// ── Section sub-components ─────────────────────────────────────────────────

function HGHero({ hg, onContact, onBack }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#050505]">
      <div className="absolute right-0 top-0 bottom-0 w-1/2 lg:w-[55%] overflow-hidden">
        <img src={HERO_IMG} alt="Holy Grail Unit" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-transparent" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/98 via-[#050505]/80 to-transparent" />
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#D4A017] via-[#D4A017]/40 to-transparent" />
      <div className="relative z-10 container mx-auto px-6 lg:px-12 max-w-7xl pt-44 pb-20">
        <motion.button {...BACK_MV} transition={T_05} onClick={onBack}
          className="flex items-center gap-2 text-sm text-white/40 hover:text-[#D4A017] transition-colors duration-200 mb-10">
          <ArrowLeft size={14} /> Back to Home
        </motion.button>
        <div className="max-w-2xl">
          <motion.p {...FU_20} transition={T_06_01} className="text-xs tracking-[0.3em] uppercase text-[#D4A017] mb-5 font-medium">{hg.hero.label}</motion.p>
          <motion.h1 {...FU_30} transition={T_08_25} className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-tight tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{hg.hero.title}</motion.h1>
          <motion.p {...FU_24} transition={T_07_45} className="text-lg text-white/60 leading-relaxed mb-10">{hg.hero.subtitle}</motion.p>
          <motion.div {...FU_16} transition={T_06_65} className="flex flex-col sm:flex-row gap-4">
            <button onClick={onContact} data-testid="hg-cta-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[#D4A017] hover:bg-[#F5B814] text-black font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-200"
              style={{ fontFamily: "'Outfit', sans-serif" }}>
              {hg.cta.btn} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <button onClick={() => document.getElementById('hg-mission')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white/80 hover:border-[#D4A017]/60 hover:text-[#D4A017] px-8 py-4 text-sm tracking-wide transition-colors duration-200">
              Discover More <ChevronDown size={15} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function HGMission({ hg }) {
  return (
    <section id="hg-mission" className="py-24 sm:py-32 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium text-center">{hg.mission.label}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white text-center leading-tight mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>{hg.mission.title}</h2>
          <blockquote className="border-l-2 border-[#D4A017] pl-8 text-base sm:text-lg text-white/70 leading-relaxed mb-8 italic">{hg.mission.body}</blockquote>
          <p className="text-base text-white/55 leading-relaxed pl-8 border-l-2 border-white/10">{hg.mission.integration}</p>
        </SectionReveal>
      </div>
    </section>
  );
}

function HGPillars({ hg }) {
  return (
    <section className="py-20 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {hg.pillars.map((pillar, i) => {
            const Icon = PILLAR_ICONS[pillar.icon];
            return (
              <SectionReveal key={pillar.title} delay={i * 120}>
                <div className="border border-white/10 p-8 bg-[#0d0d0d] hover:border-[#D4A017]/40 transition-colors duration-300 group h-full">
                  <div className="w-10 h-10 flex items-center justify-center mb-5" style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.3)' }}>
                    <Icon size={18} color="#D4A017" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{pillar.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{pillar.body}</p>
                </div>
              </SectionReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HGSpecs({ hg }) {
  return (
    <section className="py-20 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-12 font-medium text-center">Technical Specifications</p>
        </SectionReveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {hg.specs.map((spec, i) => (
            <SectionReveal key={spec.label} delay={i * 80}>
              <div className="border border-white/10 bg-[#0d0d0d] p-6 hover:border-[#D4A017]/30 transition-colors duration-300">
                <div className="text-2xl sm:text-3xl font-semibold text-[#D4A017] mb-1 tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>{spec.value}</div>
                <div className="text-xs text-white/35 uppercase tracking-widest mb-1">{spec.label}</div>
                <div className="text-xs text-white/45">{spec.desc}</div>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HGApplications({ hg }) {
  return (
    <section className="py-24 sm:py-32 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium text-center">{hg.applications.label}</p>
          <h2 className="text-3xl sm:text-4xl font-medium text-white text-center mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{hg.applications.title}</h2>
          <p className="text-base text-white/50 text-center mb-14 max-w-2xl mx-auto">{hg.applications.subtitle}</p>
        </SectionReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {hg.applications.items.map((app, i) => (
            <SectionReveal key={app.title} delay={i * 120}>
              <div className="group overflow-hidden border border-white/10 hover:border-[#D4A017]/40 transition-colors duration-300">
                <div className="h-52 overflow-hidden">
                  <img src={APP_IMAGES[i]} alt={app.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6 bg-[#0d0d0d]">
                  <div className="text-xs tracking-widest uppercase text-[#D4A017] mb-2">Application {String(i + 1).padStart(2, '0')}</div>
                  <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{app.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{app.body}</p>
                </div>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HGEnvironment({ hg }) {
  return (
    <section className="py-24 sm:py-32 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <SectionReveal>
            <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{hg.environment.label}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white leading-tight mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>{hg.environment.title}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {hg.environment.items.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full" style={{ background: 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.4)' }}>
                    <Check size={11} color="#D4A017" strokeWidth={2.5} />
                  </div>
                  <span className="text-sm text-white/70">{item}</span>
                </div>
              ))}
            </div>
          </SectionReveal>
          <SectionReveal delay={200}>
            <div className="border border-white/10 p-8 bg-[#0d0d0d] space-y-6">
              <div className="text-5xl sm:text-6xl font-semibold text-[#D4A017]" style={{ fontFamily: "'Outfit', sans-serif" }}>0</div>
              <div className="text-base text-white/40 uppercase tracking-widest text-xs">CO₂ Emissions · Chemical Waste · Acoustic Impact · Explosion Risk</div>
              <div className="h-px bg-gradient-to-r from-[#D4A017]/30 to-transparent" />
              <p className="text-sm text-white/55 leading-relaxed">{hg.bess}</p>
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
}

function HGEconomics({ hg }) {
  return (
    <section className="py-24 sm:py-32 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <SectionReveal>
            <h2 className="text-2xl sm:text-3xl font-medium text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>{hg.grid.title}</h2>
            <p className="text-base text-white/60 leading-relaxed mb-6">{hg.grid.body}</p>
            <p className="text-sm font-medium text-[#D4A017]">{hg.grid.aim}</p>
          </SectionReveal>
          <SectionReveal delay={150}>
            <div className="space-y-4">
              <div className="border border-[#D4A017]/30 bg-[#D4A017]/5 p-6">
                <div className="text-xs uppercase tracking-widest text-[#D4A017]/70 mb-2">Current Rate (10-Year Leasing)</div>
                <div className="text-4xl font-semibold text-[#D4A017]" style={{ fontFamily: "'Outfit', sans-serif" }}>€0.063/kWh</div>
                <div className="text-sm text-white/40 mt-1">per kWh — operational energy service supply</div>
              </div>
              <div className="border border-white/10 bg-[#0d0d0d] p-6">
                <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Long-Term Target</div>
                <div className="text-4xl font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>&lt; €0.05</div>
                <div className="text-sm text-white/40 mt-1">per kWh — cost reduction roadmap</div>
              </div>
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
}

function HGCTA({ hg, onContact }) {
  return (
    <section className="py-24 bg-[#080808] border-t border-white/8">
      <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">Get in Touch</p>
          <h2 className="text-3xl sm:text-4xl font-medium text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{hg.cta.title}</h2>
          <p className="text-base text-white/55 mb-10">{hg.cta.subtitle}</p>
          <button onClick={onContact} data-testid="hg-cta-bottom-btn"
            className="group inline-flex items-center gap-3 bg-[#D4A017] hover:bg-[#F5B814] text-black font-semibold px-10 py-4 text-sm tracking-wide transition-colors duration-200"
            style={{ fontFamily: "'Outfit', sans-serif" }}>
            {hg.cta.btn} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </SectionReveal>
      </div>
    </section>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

const _OG_IMG = 'https://koberholding.com/og-transformers.jpg';

const SEO_HG = {
  EN: {
    title:    'The Holy Grail of Renewable Energy | KÖBER Energy Holding',
    description: 'KÖBER Energy Holding has created an independent power generation unit delivering limitless, continuous, clean energy — available 24/7. Breakthrough renewable energy technology.',
    ogTitle:  'The Holy Grail of Renewable Energy | KÖBER Energy Holding',
    ogDesc:   'An independent unit delivering limitless, continuous, clean energy 24/7. Discover KÖBER\'s breakthrough renewable energy technology.',
    locale:   'en_US',
  },
  RO: {
    title:    'Sfântul Graal al Energiei Regenerabile | KÖBER Energy Holding',
    description: 'KÖBER Energy Holding a creat o unitate independentă de generare a energiei ce livrează energie nelimitată, continuă și curată — disponibilă 24/7. Tehnologie revoluționară.',
    ogTitle:  'Sfântul Graal al Energiei Regenerabile | KÖBER Energy Holding',
    ogDesc:   'O unitate independentă ce livrează energie nelimitată, continuă și curată 24/7. Descoperă tehnologia revoluționară KÖBER.',
    locale:   'ro_RO',
  },
};

export default function HolyGrailPage() {
  const [lang, setLang]         = useLang();
  const [contactOpen, setContactOpen] = useState(false);
  const navigate                = useNavigate();

  const handleSetLang = setLang;
  const text = t[lang];
  const hg   = text.holyGrail;
  const openContact = () => setContactOpen(true);

  useEffect(() => {
    const get = sel => document.querySelector(sel);
    const sc  = (sel, val) => { const el = get(sel); if (el) el.setAttribute('content', val); };
    const s   = SEO_HG[lang] ?? SEO_HG.EN;
    const prev = { title: document.title, desc: get('meta[name="description"]')?.content, ogT: get('meta[property="og:title"]')?.content, ogD: get('meta[property="og:description"]')?.content, ogU: get('meta[property="og:url"]')?.content, ogL: get('meta[property="og:locale"]')?.content, twT: get('meta[name="twitter:title"]')?.content, twD: get('meta[name="twitter:description"]')?.content };
    document.title = s.title;
    sc('meta[name="description"]',          s.description);
    sc('meta[property="og:title"]',         s.ogTitle);
    sc('meta[property="og:description"]',   s.ogDesc);
    sc('meta[property="og:url"]',           `${window.location.origin}/holy-grail`);
    sc('meta[property="og:locale"]',        s.locale);
    sc('meta[property="og:image"]',         _OG_IMG);
    sc('meta[name="twitter:title"]',        s.ogTitle);
    sc('meta[name="twitter:description"]',  s.ogDesc);
    sc('meta[name="twitter:image"]',        _OG_IMG);
    return () => {
      document.title = prev.title;
      sc('meta[name="description"]',        prev.desc ?? '');
      sc('meta[property="og:title"]',       prev.ogT  ?? '');
      sc('meta[property="og:description"]', prev.ogD  ?? '');
      sc('meta[property="og:url"]',          prev.ogU  ?? '');
      sc('meta[property="og:locale"]',       prev.ogL  ?? '');
      sc('meta[name="twitter:title"]',       prev.twT  ?? '');
      sc('meta[name="twitter:description"]', prev.twD  ?? '');
    };
    // SEO_HG, _OG_IMG are module-level constants; get/sc defined inside effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <div className="bg-[#050505] text-white min-h-screen">
      <Navbar text={text.nav} lang={lang} setLang={handleSetLang} onContact={openContact} />
      <HGHero   hg={hg} onContact={openContact} onBack={() => navigate('/')} />
      <HGMission hg={hg} />
      <HGPillars hg={hg} />
      <HGSpecs   hg={hg} />
      <HGApplications hg={hg} />
      <HGEnvironment  hg={hg} />
      <HGEconomics    hg={hg} />
      <HGCTA    hg={hg} onContact={openContact} />
      <Footer text={text.footer} onContact={openContact} />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} text={text.contact} lang={lang} />
    </div>
  );
}
